import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import PropTypes from 'prop-types';
import { variant } from 'styled-system';

import { BaseCallToAction } from '../../internal/components';
import { Box, Flex } from '../../layouts';

const StyledTab = styled(BaseCallToAction)(
  ({ selected }) =>
    css({
      width: '100%',
      flex: 'auto',
      border: 0,
      paddingBottom: 0,
      borderRadius: 0,
      '.container': {
        flexDirection: 'column',
        flexGrow: 1,
        py: [2, 4],
        height: '100%',
      },
      '.title': {
        color: selected ? 'primary.50' : 'neutral.70',
      },
      '.subtitle': {
        color: selected ? 'primary.50' : 'neutral.70',
      },
      ':hover:not(:disabled)': {
        '.title, .subtitle': {
          color: 'primary.50',
        },
      },
    }),
  ({ selected }) =>
    variant({
      prop: 'type',
      variants: {
        primary: {
          '.container': {
            maxWidth: '95%',
            marginBottom: 0,
            borderBottom: '4px solid',
            borderColor: selected ? 'primary.50' : 'transparent',
          },
          bg: 'white',
          ':hover:not(:disabled)': {
            bg: 'brandPrimary.0',
          },
          '.title': {
            fontWeight: 'medium',
            fontSize: ['body.1', 'body.3'],
          },
          '.subtitle': {
            fontSize: ['body.0', 'body.1'],
          },
          '&:first-of-type': {
            borderTopLeftRadius: 'inherit',
            borderBottomLeftRadius: 'inherit',
          },

          '&:last-of-type': {
            borderTopRightRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
          },
        },
        secondary: {
          '.container': {
            borderBottom: `${selected ? '4px' : '1px'} solid`,
            borderColor: selected ? 'primary.50' : 'neutral.40',
          },
          p: 0,
          bg: 'transparent',
          '.title': {
            fontWeight: 'medium',
            fontSize: 'headings.6',
          },
          '.subtitle': {
            display: 'none',
          },
        },
      },
    })
);

const Tab = forwardRef(
  (
    {
      disabled,
      onChange,
      onClick,
      onFocus,
      selected,
      selectionFollowsFocus,
      subtitle,
      title,
      value,
      type = 'primary',
      ...rest
    },
    ref
  ) => {
    const handleClick = (event) => {
      if (!selected && onChange && !disabled) {
        onChange(event, value);
      }

      if (onClick) {
        onClick(event);
      }
    };

    const handleFocus = (event) => {
      if (selectionFollowsFocus && !selected && onChange) {
        onChange(event, value);
      }

      if (onFocus) {
        onFocus(event);
      }
    };

    return (
      <StyledTab
        aria-selected={selected}
        disabled={disabled}
        fullWidth
        onClick={handleClick}
        onFocus={handleFocus}
        ref={ref}
        role="tab"
        selected={selected}
        tabIndex={selected ? 0 : -1}
        type={type}
        {...rest}
      >
        <Box className="container">
          <Flex
            alignItems="center"
            mb="2x_small"
            className="title"
            justifyContent="center"
          >
            {title}
          </Flex>
          {subtitle && (
            <Flex
              alignItems="center"
              className="subtitle"
              justifyContent="center"
            >
              {subtitle}
            </Flex>
          )}
        </Box>
      </StyledTab>
    );
  }
);

Tab.propTypes = {
  /**
   * The content of the component.
   */
  children: (props, propName, componentName, location, propFullName) => {
    if (process.env.NODE_ENV === 'production') {
      return null;
    }

    const propFullNameSafe = propFullName || propName;

    if (typeof props[propName] !== 'undefined') {
      return new Error(
        `The prop \`${propFullNameSafe}\` is not supported. Please remove it.`
      );
    }

    return null;
  },
  /**
   * if `true`, the tab is disabled
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * @ignore
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * is Tab selected
   */
  selected: PropTypes.bool,
  /**
   * the subtitle for the Tab
   */
  subtitle: PropTypes.any,
  /**
   * the title for the Tab
   */
  title: PropTypes.any.isRequired,
  /**
   * You can provide your own value. Otherwise, we fallback to the child position index.
   */
  value: PropTypes.any,
  /**
   * Tab style primary or secondary
   */
  type: PropTypes.oneOf(['primary', 'secondary']),
};

export default Tab;
